import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, Card } from '@/components/Primitives';
import { Modal, Field } from './Extensions';
import { Plus, Trash, PencilSimple } from '@phosphor-icons/react';
import { useToast } from '@/hooks/use-toast';

const DAYS = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

const TimeConditions = () => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const { toast } = useToast();

    const load = async () => {
        const r = await api.get('/time-conditions');
        setItems(r.data);
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing.id) await api.put(`/time-conditions/${editing.id}`, editing);
            else await api.post('/time-conditions', editing);
            setEditing(null); load();
            toast({ title: 'Uloženo' });
        } catch (err) {
            toast({ title: 'Chyba', description: err.response?.data?.detail || 'error', variant: 'destructive' });
        }
    };
    const del = async (id) => {
        if (!window.confirm('Smazat?')) return;
        await api.delete(`/time-conditions/${id}`); load();
    };

    return (
        <>
            <PageHeader
                eyebrow="Schedules"
                title="Time Conditions"
                description="Opakovaně použitelné pracovní doby a svátky pro Inbound Routes a Call Flows."
                actions={
                    <button onClick={() => setEditing({ name: '', timezone: 'Europe/Prague', slots: [{ days: [0,1,2,3,4], start: '09:00', end: '17:00' }], holidays: [] })}
                            data-testid="tc-create-btn"
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-[0.08em]"
                            style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>
                        <Plus size={14} weight="bold" />Nová doba
                    </button>
                }
            />
            <Card>
                {items.length === 0 ? (
                    <div className="py-8 text-center text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                        Žádné time conditions.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.1em]" style={{ color: 'var(--text-secondary)' }}>
                                <th className="text-left py-2">Název</th>
                                <th className="text-left">TZ</th>
                                <th className="text-left">Sloty</th>
                                <th className="text-left">Svátky</th>
                                <th className="text-right">Akce</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((tc) => (
                                <tr key={tc.id} className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                                    <td className="py-2">{tc.name}</td>
                                    <td className="font-mono text-xs">{tc.timezone}</td>
                                    <td className="font-mono text-xs">
                                        {tc.slots.map((s) => `${s.days.map((d) => DAYS[d]).join('')} ${s.start}-${s.end}`).join(', ')}
                                    </td>
                                    <td className="font-mono text-xs">{(tc.holidays || []).length}</td>
                                    <td className="text-right">
                                        <button onClick={() => setEditing({ ...tc })} className="p-1 hover:bg-[var(--bg-surface-hover)]" data-testid={`tc-edit-${tc.id}`}><PencilSimple size={14} /></button>
                                        <button onClick={() => del(tc.id)} className="p-1 hover:bg-[var(--bg-surface-hover)] ml-1" data-testid={`tc-delete-${tc.id}`}><Trash size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            {editing && (
                <Modal wide title={editing.id ? 'Editace' : 'Nová time condition'} onClose={() => setEditing(null)}>
                    <form onSubmit={save} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Název" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
                            <Field label="Časové pásmo" value={editing.timezone} onChange={(v) => setEditing({ ...editing, timezone: v })} mono />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Sloty</label>
                            <div className="mt-1 space-y-2">
                                {editing.slots.map((s, i) => (
                                    <div key={i} className="border p-2 grid grid-cols-12 gap-2 items-center"
                                         style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                        <div className="col-span-7 flex gap-1">
                                            {DAYS.map((d, di) => (
                                                <label key={d} className="text-xs">
                                                    <input type="checkbox" checked={s.days.includes(di)}
                                                           onChange={(e) => {
                                                               const copy = [...editing.slots];
                                                               copy[i] = { ...s, days: e.target.checked ? [...s.days, di] : s.days.filter((x) => x !== di) };
                                                               setEditing({ ...editing, slots: copy });
                                                           }} /> {d}
                                                </label>
                                            ))}
                                        </div>
                                        <input value={s.start}
                                               onChange={(e) => {
                                                   const copy = [...editing.slots];
                                                   copy[i] = { ...s, start: e.target.value };
                                                   setEditing({ ...editing, slots: copy });
                                               }}
                                               className="col-span-2 px-2 py-1 bg-[var(--bg-app)] border text-xs font-mono text-center"
                                               style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                                        <input value={s.end}
                                               onChange={(e) => {
                                                   const copy = [...editing.slots];
                                                   copy[i] = { ...s, end: e.target.value };
                                                   setEditing({ ...editing, slots: copy });
                                               }}
                                               className="col-span-2 px-2 py-1 bg-[var(--bg-app)] border text-xs font-mono text-center"
                                               style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                                        <button onClick={() => setEditing({ ...editing, slots: editing.slots.filter((_, j) => j !== i) })}
                                                className="col-span-1 text-red-400">
                                            <Trash size={12} />
                                        </button>
                                    </div>
                                ))}
                                <button type="button"
                                        onClick={() => setEditing({ ...editing, slots: [...editing.slots, { days: [0,1,2,3,4], start: '09:00', end: '17:00' }] })}
                                        className="text-xs px-2 py-1 border" style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>
                                    + Slot
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase font-bold" style={{ color: 'var(--text-secondary)' }}>Svátky (YYYY-MM-DD, oddělené čárkou)</label>
                            <input value={(editing.holidays || []).join(',')}
                                   onChange={(e) => setEditing({ ...editing, holidays: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })}
                                   className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-app)] border text-sm font-mono"
                                   style={{ borderColor: 'var(--border-default)', borderRadius: 2 }} />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setEditing(null)} className="px-3 py-2 text-xs font-bold uppercase border" style={{ borderColor: 'var(--border-default)', borderRadius: 2 }}>Zrušit</button>
                            <button type="submit" data-testid="tc-save-btn" className="px-3 py-2 text-xs font-bold uppercase" style={{ background: 'var(--brand)', color: '#fff', borderRadius: 2 }}>Uložit</button>
                        </div>
                    </form>
                </Modal>
            )}
        </>
    );
};

export default TimeConditions;
